import React, { useEffect, useRef, useState } from 'react';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import { useShareDataset } from './useShareDataset';
import { MobileShareSheet } from './MobileShareSheet';
import { DesktopSharePopover } from './DesktopSharePopover';

interface ShareDatasetModalProps {
  isOpen: boolean;
  onClose: () => void;
  tableId: string;
  tableName: string;
}

export const ShareDatasetModal: React.FC<ShareDatasetModalProps> = ({ isOpen, onClose, tableId, tableName }) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });
  const isMobile = useIsMobile();
  const share = useShareDataset(tableId, tableName, isOpen);

  useEffect(() => {
    if (isOpen) {
      const anchor = document.getElementById('dataset-share-btn');
      if (anchor) {
        const rect = anchor.getBoundingClientRect();
        setPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
      } else {
        setPosition({ top: 80, right: 20 });
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) onClose();
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }, 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return isMobile
    ? <MobileShareSheet share={share} tableName={tableName} onClose={onClose} popoverRef={popoverRef} />
    : <DesktopSharePopover share={share} tableName={tableName} position={position} popoverRef={popoverRef} />;
};
